import { memo, useRef } from 'react'

import { COURT, shotAria, shotNote, splitText, visibleDots } from './shotModel.js'

// The shot chart: a half-court with every shot of the game, made filled and missed hollow. P6-330b (GN-3b).
//
// Hand-built SVG in the court's own units (feet, `COURT`: 50 wide and 47 long, the basket at the origin), the design's
// markings path for path, no library. The dots come from `shotModel.js::shotChart`, which has already turned every
// right-basket shot through 180 degrees (see that file for the rotation, and for what it cannot settle about which
// side of the picture is the shooter's right).
//
// ═══════════════════════════════════════════════════════════════════════════
// IT DRAWS ITSELF IN, ONCE PER DOT, AND A DOT THAT IS ALREADY THERE IS NOT DRAWN AGAIN
// ═══════════════════════════════════════════════════════════════════════════
//
// A dot pops in (`d2dHoopsShotPop`, in the stylesheet) with a delay that staggers the dots in the order they were
// taken. The design staggers 22ms a dot, which is three quarters of a second for the thirty-odd shots it held; this
// chart holds EVERY shot, two hundred and more by the end of a game, and 22ms each would take four and a half
// seconds to draw. So the stagger is capped: 22ms a dot while that fits in `DRAW_IN_MS` and the whole batch inside it
// otherwise. A delay is worked out ONCE per dot, when the dot is first drawn (`delays`), and kept: the page
// re-renders every second and the game is re-read every fifteen, and a dot that is already on the court must neither
// vanish nor pop again. A shot that arrives with a poll is the only fresh dot, and it pops on its own.
//
// ⚠️ THE FILTER REMOUNTS THE DOTS IT BRINGS BACK (away, then both: the home team's dots are drawn in again, as the
// design's are), and leaving the section and coming back draws the whole chart in again. That is the chart drawing
// itself in, which is what was asked for, and it is the same on every open.
//
// ⚠️ A USER WHO ASKS FOR LESS MOTION GETS NONE: `prefers-reduced-motion` turns the animation off in the stylesheet, so
// the dots are simply there (the designed off state), not invisible for as long as their delay would have been.

/** The stagger between dots, and the most the whole draw-in may take, in milliseconds (the pop itself is on top). */
export const DOT_STEP_MS = 22
export const DRAW_IN_MS = 1600

const FILTERS = (away, home) => [
  ['both', 'Both'],
  ['away', away],
  ['home', home],
]

/** The half-court's markings, in feet (the design's own, path for path). */
function Court() {
  return (
    <g className="d2d-hoops__court" data-testid="hoops-court">
      <rect x="-25" y="-5.25" width="50" height="47" />
      <rect x="-8" y="-5.25" width="16" height="19" />
      <circle cx="0" cy="13.75" r="6" />
      <path d="M -4 0 A 4 4 0 0 0 4 0" />
      <path d="M -22 -5.25 L -22 8.95 A 23.75 23.75 0 0 0 22 8.95 L 22 -5.25" />
      <line x1="-3" y1="-1.25" x2="3" y2="-1.25" />
      <circle cx="0" cy="0" r="0.75" />
      <path d="M -6 41.75 A 6 6 0 0 1 6 41.75" />
    </g>
  )
}

/**
 * `chart` is `shotModel.js::shotChart`'s answer (memoised on the game's object by the caller), `side` is the filter
 * in force and `onSide(side)` changes it, `away` and `home` are the tricodes (two strings, not an object, so that a
 * second of the page's clock ticking does not make this a new set of props) and `live` says whether the newest dot
 * is highlighted and the note says `So far`.
 */
function ShotChart({ chart, side, onSide, away, home, live }) {
  // A dot's delay, kept for as long as this chart is on the page. Written during render, and idempotent: the same key
  // always gets the same number, so a second render of the same props (StrictMode's) reads what the first wrote.
  const delays = useRef(new Map())
  const visible = visibleDots(chart, side)
  const fresh = visible.filter((dot) => !delays.current.has(dot.key)).length
  const step = Math.min(DOT_STEP_MS, DRAW_IN_MS / Math.max(1, fresh))
  let order = 0
  const dots = visible.map((dot, index) => {
    let delay = delays.current.get(dot.key)
    if (delay === undefined) {
      delay = Math.round(order * step)
      order += 1
      delays.current.set(dot.key, delay)
    }
    const newest = live && index === visible.length - 1
    return (
      <circle
        key={dot.key}
        className="d2d-hoops__shot"
        data-testid="hoops-shot"
        data-side={dot.side ?? 'none'}
        data-made={dot.made ? 'true' : 'false'}
        data-newest={newest ? 'true' : 'false'}
        cx={dot.cx}
        cy={dot.cy}
        r={newest ? 1.15 : 0.85}
        style={{ '--hoops-delay': `${delay}ms` }}
      />
    )
  })

  return (
    <section className="d2d-hoops__panel" aria-label="Shot chart" data-testid="hoops-shots">
      <div className="d2d-hoops__panelhead">
        <h3 className="d2d-hoops__sectiontitle d2d-micro">Shot chart</h3>
        <span className="d2d-hoops__panelnote d2d-micro" data-testid="hoops-shot-note">
          {shotNote(chart, live)}
        </span>
      </div>

      <div className="d2d-hoops__filters" role="group" aria-label="Show shots by">
        {FILTERS(away, home).map(([key, label]) => (
          <button
            type="button"
            key={key}
            className="d2d-hoops__filter"
            aria-pressed={side === key ? 'true' : 'false'}
            data-testid={`hoops-shot-filter-${key}`}
            onClick={() => onSide(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="d2d-hoops__shotcourt">
        <svg
          viewBox={`${COURT.left} ${COURT.top} ${COURT.width} ${COURT.height}`}
          role="img"
          aria-label={shotAria(chart)}
          focusable="false"
          data-testid="hoops-shot-svg"
        >
          <Court />
          <g>{dots}</g>
        </svg>
      </div>

      <div className="d2d-hoops__shotlegend d2d-micro">
        <span className="d2d-hoops__legenditem">
          <span className="d2d-hoops__shotkey d2d-hoops__shotkey--made" aria-hidden="true" />
          Made
        </span>
        <span className="d2d-hoops__legenditem">
          <span className="d2d-hoops__shotkey d2d-hoops__shotkey--missed" aria-hidden="true" />
          Missed
        </span>
        {[
          ['away', away],
          ['home', home],
        ].map(([which, tricode]) => (
          <span className="d2d-hoops__shotsplit" key={which} data-side={which} data-testid={`hoops-shot-split-${which}`}>
            {splitText(tricode, chart.splits[which])}
          </span>
        ))}
      </div>
    </section>
  )
}

export default memo(ShotChart)
